import TenantQualifications from "shared/icons/tenantQualifications.svg?react";
import { cx } from "shared/utils";
import { ApplicantType, ApplicationStatus } from "shared/types";
import { VerificationSchemaView } from "../../slices/types";
import { useNavigate } from "react-router-dom";
import { LeftPane } from "./LeftPane";
import {
  Select,
  Hide,
  Show,
  Button,
  ButtonSize,
  ButtonVariant
} from "shared/components";
import { formatTitle } from "utils/formatTitle";
import {
  ArrowLeft,
  Award03,
  CurrencyDollar,
  FileCheck02,
  Grid01,
  Mail02,
  Settings02
} from "untitledui-js-base";
import { useTemplateContext } from "../../provider";
import { Tabs } from "shared/components/Tabs";
import { FC, useEffect, useRef } from "react";
import { Template } from "../../../../../types/landlord/verificationTemplate";
import { TemplateMenu } from "../TemplateMenu";
import { screeningServicesTabs } from "../../constant";
import { DiscountTag02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { ChangeTemplateSubmenu } from "../ChangeTemplateSubmenu";
import { useAuthContext } from "providers/Auth";

interface Props {
  isModal?: boolean;
  error?: string;
  clearError?: () => void;
  isEditing?: boolean;
  template?: Template;
  setEditedName?: (x: string) => void;
  editedName: string;
  toggleEditMode?: () => void;
  handleDuplicate?: () => void;
  setShowDeleteModal?: (v: boolean) => void;
  applicationId?: string;
  applicationStatus?: ApplicationStatus;
}

export const SideBar: FC<Props> = ({
  isModal,
  error,
  clearError,
  template,
  editedName,
  setEditedName,
  toggleEditMode,
  handleDuplicate,
  setShowDeleteModal,
  applicationId,
  applicationStatus
}) => {
  const navigate = useNavigate();
  const {
    readOnly,
    createMode,
    partnerType,
    setPartnerType,
    combinedVerificationSchemas: verificationSchemas,
    setSelectedVerificationSchema,
    selectedVerificationSchema
  } = useTemplateContext();
  const {
    type: { hasTemplateFeeCollection }
  } = useAuthContext();
  const editInputRef = useRef<HTMLInputElement>(null);

  const selectTab = (tabName: ApplicantType) => {
    setSelectedVerificationSchema(VerificationSchemaView.ScreeningServices);
    setPartnerType(tabName);
  };

  useEffect(() => {
    if (error && editInputRef.current) editInputRef.current.focus();
  }, [error]);

  return (
    <>
      <div className="md:hidden p-4">
        <Select
          id="settingsView"
          onChange={(e) =>
            setSelectedVerificationSchema(
              e.target.value as VerificationSchemaView
            )
          }
          value={selectedVerificationSchema}
          options={[
            {
              value: VerificationSchemaView.TenantQualifications,
              label: "Tenant qualifications"
            },
            {
              value: VerificationSchemaView.DecisionCriteria,
              label: "Decision criteria"
            },
            {
              value: VerificationSchemaView.SubmissionDisclosure,
              label: "Submission disclosure"
            },
            {
              value: VerificationSchemaView.General,
              label: "Other settings"
            },
            {
              value: VerificationSchemaView.ScreeningServices,
              label: "Screening services"
            },
            ...(hasTemplateFeeCollection
              ? [
                  {
                    value: VerificationSchemaView.AdditionalFees,
                    label: "Additional fees"
                  }
                ]
              : []),
            {
              value: VerificationSchemaView.AdditionalQuestions,
              label: "Additional questions"
            },
            ...verificationSchemas.map((schema) => ({
              value: String(schema.name),
              label: formatTitle(schema.name)
            }))
          ]}
        />
        <Tabs
          selected={partnerType}
          tabs={[
            ApplicantType.Applicant,
            ApplicantType.CoSigner,
            ApplicantType.Occupant
          ]}
          setActiveTab={(t) => selectTab(t)}
          displayMap={{
            [ApplicantType.Applicant]: "Applicant",
            [ApplicantType.CoSigner]: "Guarantor",
            [ApplicantType.Occupant]: "Occupant"
          }}
        />
      </div>
      <div
        className={cx(
          "hide-scroll md:block hidden sticky w-[290px] overflow-y-auto rounded-l-lg px-4 py-4"
        )}
      >
        <Hide if={isModal}>
          <Button
            variant={ButtonVariant.BASE}
            size={ButtonSize.SM}
            borderLess
            Icon={ArrowLeft}
            onClick={() => navigate("/verification-templates")}
            className="flex items-center gap-2 text-xs font-bold hover:bg-alpha-40 rounded-lg p-2"
          >
            Templates
          </Button>
          <div className="my-4 border-b border-alpha-40" />
        </Hide>

        <div className="flex justify-between items-center py-2">
          <div className="space-y-2">
            <p
              className={cx(
                "text-sm",
                error ? "text-danger-600" : "text-content-400"
              )}
            >
              Screening template
            </p>
            {readOnly ? (
              <h1 className="font-bold text-content-1000 text-lg truncate max-w-[175px]">
                {editedName}
              </h1>
            ) : (
              <div className="flex w-full items-center">
                <div className="flex w-full items-center">
                  <div className="relative min-w-[1em] w-min flex items-center">
                    <span className="invisible whitespace-pre text-lg font-bold text-content-1000 h-10 max-w-[130px] overflow-hidden text-ellipsis">
                      {editedName || "Untitled"}
                    </span>
                    <input
                      ref={editInputRef}
                      className={cx(
                        "absolute left-0 w-full text-lg border-none p-0 font-bold focus:outline-none focus:ring-0 max-w-[130px] overflow-hidden text-ellipsis",
                        error
                          ? "text-danger-600 placeholder:text-danger-600"
                          : "text-content-1000 hover:text-content-400 focus:hover:text-content-1000"
                      )}
                      onChange={(e) => {
                        if (setEditedName) {
                          setEditedName(e.target.value);
                          clearError && clearError();
                        }
                      }}
                      value={editedName}
                      onFocus={(e) => {
                        e.preventDefault();
                        if (toggleEditMode) toggleEditMode();

                        const input = e.target;
                        const length = input.value.length;

                        // Use requestAnimationFrame to ensure this runs after the default click behavior
                        requestAnimationFrame(() => {
                          input.setSelectionRange(length, length);
                        });
                      }}
                      onBlur={() => {
                        if (toggleEditMode) toggleEditMode();
                      }}
                      placeholder="Untitled"
                      readOnly={readOnly}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <Hide if={createMode}>
            <TemplateMenu
              template={template}
              onDelete={
                setShowDeleteModal ? () => setShowDeleteModal(true) : undefined
              }
              onDuplicate={
                handleDuplicate ? () => handleDuplicate() : undefined
              }
            />
          </Hide>
        </div>

        <Hide if={!applicationId}>
          <ChangeTemplateSubmenu
            applicationId={applicationId || ""}
            applicationStatus={applicationStatus}
            currentTemplate={template || undefined}
            contentClassName="!z-[105] max-h-[300px] overflow-y-auto"
          />
        </Hide>

        <div className="my-4 border-b border-alpha-40" />
        <div className="space-y-1 max-h-[calc(100vh-220px)] overflow-y-auto">
          <LeftPane
            onClick={(v) => selectTab(v as ApplicantType)}
            tabName={VerificationSchemaView.ScreeningServices}
            active={screeningServicesTabs.includes(selectedVerificationSchema)}
            selectedTab={selectedVerificationSchema}
            selectedChild={partnerType}
            Icon={<Grid01 size="20" className="stroke-content-500" />}
            childItems={[
              ApplicantType.Applicant,
              ApplicantType.CoSigner,
              ApplicantType.Occupant
            ]}
          />
          <LeftPane
            onClick={() =>
              setSelectedVerificationSchema(
                VerificationSchemaView.DecisionCriteria
              )
            }
            tabName={VerificationSchemaView.DecisionCriteria}
            selectedTab={selectedVerificationSchema}
            Icon={
              <Award03
                size="20"
                className={cx(
                  "stroke-content-500",
                  selectedVerificationSchema ===
                    VerificationSchemaView.DecisionCriteria &&
                    "stroke-primary-500 text-primary-600"
                )}
              />
            }
            disabled={createMode}
            disabledMessage="To configure screening criteria, first create a template"
          />
          <LeftPane
            onClick={() =>
              setSelectedVerificationSchema(
                VerificationSchemaView.TenantQualifications
              )
            }
            tabName={VerificationSchemaView.TenantQualifications}
            selectedTab={selectedVerificationSchema}
            Icon={<TenantQualifications />}
          />
          <LeftPane
            onClick={() =>
              setSelectedVerificationSchema(
                VerificationSchemaView.SubmissionDisclosure
              )
            }
            tabName={VerificationSchemaView.SubmissionDisclosure}
            selectedTab={selectedVerificationSchema}
            Icon={
              <FileCheck02
                size="20"
                className={cx(
                  "stroke-content-500",
                  selectedVerificationSchema ===
                    VerificationSchemaView.SubmissionDisclosure &&
                    "stroke-primary-500 text-primary-600"
                )}
              />
            }
          />
          <LeftPane
            onClick={() =>
              setSelectedVerificationSchema(
                VerificationSchemaView.DiscountCodes
              )
            }
            tabName={VerificationSchemaView.DiscountCodes}
            selectedTab={selectedVerificationSchema}
            Icon={
              <HugeiconsIcon
                icon={DiscountTag02Icon}
                size={20}
                className={cx(
                  "stroke-content-500",
                  selectedVerificationSchema ===
                    VerificationSchemaView.DiscountCodes &&
                    "stroke-primary-500 text-primary-600"
                )}
              />
            }
          />
          <Show if={hasTemplateFeeCollection}>
            <LeftPane
              onClick={() =>
                setSelectedVerificationSchema(
                  VerificationSchemaView.AdditionalFees
                )
              }
              tabName={VerificationSchemaView.AdditionalFees}
              selectedTab={selectedVerificationSchema}
              Icon={
                <CurrencyDollar
                  size="20"
                  className={cx(
                    "stroke-content-500",
                    selectedVerificationSchema ===
                      VerificationSchemaView.AdditionalFees &&
                      "stroke-primary-500 text-primary-600"
                  )}
                />
              }
            />
          </Show>
          <LeftPane
            onClick={() =>
              setSelectedVerificationSchema(
                VerificationSchemaView.AdverseAction
              )
            }
            tabName={VerificationSchemaView.AdverseAction}
            selectedTab={selectedVerificationSchema}
            Icon={
              <Mail02
                size="20"
                className={cx(
                  "stroke-content-500",
                  selectedVerificationSchema ===
                    VerificationSchemaView.AdverseAction &&
                    "stroke-primary-500 text-primary-600"
                )}
              />
            }
          />
          <LeftPane
            onClick={() =>
              setSelectedVerificationSchema(VerificationSchemaView.General)
            }
            tabName={VerificationSchemaView.General}
            selectedTab={selectedVerificationSchema}
            Icon={
              <Settings02
                size="20"
                className={cx(
                  "stroke-content-500",
                  selectedVerificationSchema ===
                    VerificationSchemaView.General &&
                    "stroke-primary-500 text-primary-600"
                )}
              />
            }
          />
        </div>
      </div>
    </>
  );
};
